import * as React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Box, Button, Paper, Tooltip, Typography } from '@material-ui/core';
import { CHASAdvancedLogo, CHASEliteLogo, CHASFoundationLogo, CHASStandardLogo } from '../../images/subscription-logos';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../hooks/usePermission';
import { ActivityAction } from '@pec/aion-ui-core/interfaces/activities';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../combineReducers';
import { activateFoundationSubscription } from '../../slices/subscriptionsV2';
import { LoadingOverlay } from '@pec/aion-ui-components/components/LoadingOverlay';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(2)
    },
    mainTitle: {
      marginBottom: theme.spacing(-0.5)
    },
    title: {
      letterSpacing: 0.25,
      marginBottom: theme.spacing(1)
    },
    description: {
      marginTop: theme.spacing(2.5),
      letterSpacing: 0.25
    },
    paper: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(3)
    },
    mainIcon: {
      width: 158
    },
    smallIcon: {
      width: 71
    },
    button: {
      letterSpacing: 1.25
    }
  })
);

type Props = {
  organizationId: string;
  subscriptionId: string;
};
export const FoundationBanner: React.FC<Props> = ({ organizationId, subscriptionId }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { hasSubscriptionsPermission } = usePermission(ActivityAction.Write);
  const [isLoading, setIsLoading] = React.useState(false);

  const activateFoundation = async () => {
    setIsLoading(true);
    await dispatch(activateFoundationSubscription({ organizationId, subscriptionId }));
    setIsLoading(false);
  };

  const goToFoundationUpgrade = () => {
    history.push(`/${organizationId}/upgrade?foundation=true`);
  };

  return (
    <Box className={classes.container}>
      <Typography variant="h6" classes={{ root: classes.mainTitle }}>
        {t('subscriber:foundationBanner.chooseMembership', 'Choose your CHAS membership:')}
      </Typography>
      <LoadingOverlay isLoading={isLoading}>
        <Paper elevation={3} classes={{ root: classes.paper }}>
          <Box display="flex">
            <Box className={classes.mainIcon} flexShrink={0}>
              <CHASFoundationLogo />
            </Box>
            <Box marginLeft={2} marginBottom={3}>
              <Typography variant="h5" classes={{ root: classes.title }}>
                {t('subscriber:foundationBanner.chasFoundation', 'CHAS Foundation')}
              </Typography>
              <Typography variant="body2" classes={{ root: classes.description }}>
                {t(
                  'subscriber:foundationBanner.chasFoundationDescription1',
                  'Welcome to CHAS Foundation, our free launchpad for contractors who want to start the certification process but need more time and resources.'
                )}
              </Typography>
              <Typography variant="body2" classes={{ root: classes.description }}>
                {t(
                  'subscriber:foundationBanner.chasFoundationDescription2',
                  'All companies are legally required to complete certain legal documentation, and at CHAS, we will help you comply with legal requirements and get assessment-ready. CHAS Foundation allows you to learn what maintenance checklists to use and talks you through legal requirements and writing policies—all while offering advice on how to comply in areas of risk management.'
                )}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" justifyContent="center">
            <Tooltip
              title={t(
                'subscriber:foundationBanner.contactYourAdministrator',
                "Contact your company's administrator to update your membership"
              ).toString()}
              disableHoverListener={hasSubscriptionsPermission}
              disableTouchListener={hasSubscriptionsPermission}
            >
              <span>
                <Button
                  color="primary"
                  variant="contained"
                  classes={{ root: classes.button }}
                  disabled={!hasSubscriptionsPermission}
                  onClick={activateFoundation}
                >
                  {t('subscriber:foundationBanner.activateFoundation', 'Activate Foundation')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      </LoadingOverlay>
      <Paper elevation={3} classes={{ root: classes.paper }}>
        <Box display="flex">
          <Box display="flex" flexDirection="column" alignItems="center" className={classes.mainIcon} flexShrink={0}>
            <Box className={classes.smallIcon} marginBottom={-0.75}>
              <CHASStandardLogo />
            </Box>
            <Box display="flex" marginTop={-0.75}>
              <Box className={classes.smallIcon} marginRight={0.25}>
                <CHASAdvancedLogo />
              </Box>
              <Box className={classes.smallIcon} marginLeft={0.25}>
                <CHASEliteLogo />
              </Box>
            </Box>
          </Box>
          <Box marginLeft={2} marginBottom={3}>
            <Typography variant="h5" classes={{ root: classes.title }}>
              {t('subscriber:foundationBanner.certifiedMembership', 'Certified Membership')}
            </Typography>
            <Typography variant="body2" classes={{ root: classes.description }}>
              {t(
                'subscriber:foundationBanner.certifiedMembershipDescription',
                'Become a CHAS member and ensure your business stays up-to-date on ever-changing risk management in the UK. Compliance with CHAS helps get you noticed by top clients, showcasing your quality and care towards your business and workers. Help build your trustworthy reputation today by becoming a CHAS member and gaining certification. Our award-winning team is always on hand to talk you through the process.'
              )}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" justifyContent="center">
          <Tooltip
            title={t(
              'subscriber:foundationBanner.contactYourAdministrator',
              "Contact your company's administrator to update your membership"
            ).toString()}
            disableHoverListener={hasSubscriptionsPermission}
            disableTouchListener={hasSubscriptionsPermission}
          >
            <span>
              <Button
                color="primary"
                variant="contained"
                classes={{ root: classes.button }}
                disabled={!hasSubscriptionsPermission}
                onClick={goToFoundationUpgrade}
              >
                {t('subscriber:foundationBanner.purchaseMembership', 'Purchase Membership')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
};
